import React from "react";
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import type {
    Formula,
    FormulaInput,
    FormulaRow,
    FormulaRowInput,
    UUID,
    Variable
} from "../../base/ui_general/GeneralInterfaces.tsx";
import {RBSCard, RBSCardVariants} from '../../base/rbs_components/RBSCard';
import {RBSButton, RBSButtonSizes, RBSButtonVariants} from '../../base/rbs_components/RBSButton';
import {Col, Row} from 'react-bootstrap';
import autobind from "autobind-decorator";
import {BaseComponent, type BaseComponentState} from "../../base/ui_general/BaseComponent.tsx";
import {
    ApiEndPoints,
    FormulaOperatorEnum,
    FormulaTypeEnum, FormulaVariablePreProcessingTypeEnum,
    LabelTextMessageTypes,
    ValidationSchemes,
    WaterfallProcessableTypeEnum,
} from "../../base/Settings.tsx";
import {getRequest_create_formula, getRequest_delete_formula, getRequest_update_formula} from "../../api/formula.tsx";
import {
    getRequest_create_formula_row,
    getRequest_delete_formula_row,
    getRequest_update_formula_row
} from "../../api/formula_row.tsx";
import {createSuccessFeedbackMessage} from '../../base/utils/GeneralUtilities';
import {InputStateControl} from "../../base/utils/InputStateControl.tsx";
import {FormulaEditModal} from "../modals/FormulaEditModal.tsx";
import {VariablesBadges} from "../../base/ui_general/GeneralFunctionComponents.tsx";
import styles from './TabShared.module.css';
import {TabCardHeader} from './components/TabCardHeader';
import {EmptyStateRow} from './components/EmptyStateRow';
import {VariableDropCell} from './components/VariableDropCell';


/**
 * Provides readable labels for operators used in formula rows.
 */
const operatorLabelMap: { [key in FormulaOperatorEnum]: string } = {
    [FormulaOperatorEnum.PLUS]: "+",
    [FormulaOperatorEnum.MINUS]: "-",
    [FormulaOperatorEnum.MULTIPLY]: "*",
    [FormulaOperatorEnum.DIVIDE]: "/",
    [FormulaOperatorEnum.PLUS_BRACKET]: ")+(",
    [FormulaOperatorEnum.MINUS_BRACKET]: ")-(",
    [FormulaOperatorEnum.MULTIPLY_BRACKET]: ")*(",
    [FormulaOperatorEnum.DIVIDE_BRACKET]: ")/(",
    [FormulaOperatorEnum.MAX]: ") max (",
    [FormulaOperatorEnum.MIN]: ") min ("
};

/**
 * Provides readable labels for types used in formula rows.
 */
const variablePreProcessingLabelMap: { [key in FormulaVariablePreProcessingTypeEnum]: string } = {
    [FormulaVariablePreProcessingTypeEnum.TEMPORAL_MAX]: "time max",
    [FormulaVariablePreProcessingTypeEnum.TEMPORAL_MIN]: "time min",
    [FormulaVariablePreProcessingTypeEnum.TEMPORAL_SUM]: "time sum",
    [FormulaVariablePreProcessingTypeEnum.TEMPORAL_MEAN]: "time mean",
    [FormulaVariablePreProcessingTypeEnum.PREVIOUS_PERIOD]: "previous step"
};

/**
 * Props for the FormulasTab component, extending generic tab props.
 */
interface FormulasTabProps extends DesignCenterTabProps {
    /** List of formulas to display and edit */
    formulas: Array<Formula>;
    /** List of variables to assign to formula rows */
    variables: Array<Variable>;
}

/**
 * Structure for referring to registered input fields per row.
 */
interface RowInputStateIds {
    variableId: number;
    numberId: number;
}

/**
 * State type for the FormulasTab component.
 */
interface FormulasTabState extends BaseComponentState {
    /** Formulas being displayed/edited (may include unsaved items) */
    localFormulas: Array<Formula>;
    /** Counter for temporary/new row IDs */
    nextRowId: number;
    /** Currently hovered formula row ID */
    hoveredRow: string | undefined;
    /** Row currently being dragged */
    draggedRow: { formulaId: string; rowId: string; variableName: string } | null;
    /** Edit formula modal open/closed */
    showEditFormulaModal: boolean;
    /** Which formula is being edited, if any */
    editingFormula: Formula | undefined;
    /** Is form input in modal valid? */
    editingFormulaValid: boolean;
    /** Which formula is being dragged (reordering) */
    draggedFormulaId: string | null;
    /** Registered input controls for formula rows */
    rowInputStateIds: Record<string, RowInputStateIds>;
}

/**
 * The FormulasTab component provides:
 * - Listing and editing of formulas.
 * - Adding/removing/reordering formulas and rows.
 * - Edit modal for formulas.
 * - Drag-and-drop variable assignment.
 */
export class FormulasTab extends BaseComponent<FormulasTabProps, FormulasTabState> {
    /** Input state control for formula rows */
    private rowInputStateControl = new InputStateControl();
    private editingFormulaInputControl = new InputStateControl();


    constructor(props: FormulasTabProps) {
        super(props);

        // Setup input control used in the "Edit Formula" modal
        this.editingFormulaInputControl = new InputStateControl();
        this.editingFormulaInputControl.setGlobalValidityUpdateCallback(
            (isValid: boolean) => this.setState({editingFormulaValid: isValid})
        );
        this.state = {
            ...this.state,
            localFormulas: props.formulas,
            nextRowId: 1,
            hoveredRow: undefined,
            draggedRow: null,
            showEditFormulaModal: false,
            editingFormula: undefined,
            editingFormulaValid: true,
            draggedFormulaId: null,
            rowInputStateIds: {},
        };
    }

    /**
     * Register input controls for a new/existing formula row.
     * Ensures validation and state is tracked for variable/factor fields.
     */
    private registerRowInputs(row: FormulaRow) {
        const rowKey = row.id;
        if (!this.state.rowInputStateIds[rowKey]) {
            const variableId = this.rowInputStateControl.registerComponent(
                rowKey + "-variable",
                [ValidationSchemes.REQUIRED]
            );
            const numberId = this.rowInputStateControl.registerComponent(
                rowKey + "-number",
                [ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]
            );
            this.rowInputStateControl.updateValue(variableId, row.variable_id || "");
            this.setState(state => ({
                rowInputStateIds: {
                    ...state.rowInputStateIds,
                    [rowKey]: {variableId, numberId}
                }
            }));
        }
    }

    /**
     * Synchronize formulas if incoming props change (e.g. after server update)
     */
    componentDidUpdate(prevProps: FormulasTabProps) {
        if (prevProps.formulas !== this.props.formulas) {
            const newRows: FormulaRow[] = [];
            (this.props.formulas || []).forEach(formula => {
                (formula.formula_rows || []).forEach(row => {
                    if (!this.state.rowInputStateIds[row.id]) newRows.push(row);
                });
            });
            newRows.forEach(row => this.registerRowInputs(row));
            this.setState({localFormulas: this.props.formulas || []});
        }
    }

    @autobind
    handleDeleteFormula(formula_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_formula(formula_id),
            () => {
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FORMULA_DELETED));
                this.props.loadData([
                    ApiEndPoints.FORMULA,
                    ApiEndPoints.VARIABLE
                ]);
            }
        );
    }


    @autobind
    handleDeleteFormulaRow(formula_row_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_formula_row(formula_row_id),
            () => {
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FORMULA_ROW_DELETED));
                this.props.loadData([ApiEndPoints.FORMULA]);
            }
        );
    }

    /** Close the formula edit modal */
    @autobind
    closeEditFormulaModal() {
        this.setState({
            showEditFormulaModal: false,
            editingFormula: undefined,
            editingFormulaValid: true,
        });
    }

    /** Open modal to edit a formula's name/type/description */
    @autobind
    openEditFormulaModal(formula: Formula) {
        this.setState({
            showEditFormulaModal: true,
            editingFormula: formula,
            editingFormulaValid: true,
        });
    }

    /**
     * Save changes from the edit formula modal to local state and backend
     */
    @autobind
    handleEditFormulaSubmit(formulaInput: FormulaInput) {
        const {editingFormula, localFormulas} = this.state;
        if (!editingFormula) return;
        const updatedFormula = {...editingFormula, ...formulaInput};
        const updatedFormulas = localFormulas.map(f =>
            f.id === editingFormula.id ? updatedFormula : f
        );
        this.setState({localFormulas: updatedFormulas});
        this.sendUpdateFormula(updatedFormula);
        this.closeEditFormulaModal();
    }

    /** Initiate a formula drag event for drag-and-drop reordering */
    @autobind
    handleFormulaDragStart(e: React.DragEvent<HTMLDivElement>, formulaId: string) {
        e.dataTransfer.effectAllowed = "move";
        this.setState({draggedFormulaId: formulaId});
    }

    /** Handle dropping a formula onto another (reordering them) */
    @autobind
    handleFormulaDrop(e: React.DragEvent<HTMLDivElement>, targetFormulaId: string) {
        e.preventDefault();
        const {draggedFormulaId} = this.state;
        if (!draggedFormulaId || draggedFormulaId === targetFormulaId) {
            this.setState({draggedFormulaId: null});
            return;
        }
        this.moveFormula(draggedFormulaId, targetFormulaId);
    }

    /**
     * Move/reorder a formula within the list, update all order fields, and persist to backend.
     */
    private moveFormula(draggedId: string, targetFormulaId: string) {
        const formulas = [...this.state.localFormulas];
        const draggedIndex = formulas.findIndex(t => t.id === draggedId);
        const targetIndex = formulas.findIndex(t => t.id === targetFormulaId);
        if (draggedIndex === -1 || targetIndex === -1) return;

        const [draggedFormula] = formulas.splice(draggedIndex, 1);
        const insertAt = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
        formulas.splice(insertAt, 0, draggedFormula);

        // Reassign new processing_order sequentially
        formulas.forEach((t, i) => t.processing_order = i + 1);

        this.setState({localFormulas: formulas, draggedFormulaId: null});
        formulas.forEach(t => this.sendUpdateFormula(t));
    }

    /** Capture drag-over events for allowing drops (required for DnD API) */
    @autobind
    handleFormulaDragOver(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
    }

    /**
     * Call backend to update a formula after changes (e.g. order, editing)
     */
    @autobind
    private sendUpdateFormula(updatedFormula: Formula) {
        this.processFetchRequest(
            getRequest_update_formula(updatedFormula),
            () => {
                this.props.loadData([
                    ApiEndPoints.FORMULA,
                    ApiEndPoints.VARIABLE
                ]);
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FORMULA_UPDATED));
            }
        );
    }

    /**
     * Call backend to update a row after local changes.
     */
    @autobind
    private sendUpdateRow(updatedRow: FormulaRow) {
        this.processFetchRequest(
            getRequest_update_formula_row(updatedRow),
            () => {
                this.props.loadData([ApiEndPoints.FORMULA]);
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FORMULA_ROW_UPDATED));
            }
        );
    }

    /**
     * Add a new, unsaved formula with a single row (name auto-incremented)
     */
    @autobind
    handleAddFormula() {
        if (!this.props.transaction) return;

        const {localFormulas, nextRowId} = this.state;


        const baseName = "New Formula";
        const regex = new RegExp(`^${baseName}(\\s(\\d+))?$`);
        let maxNum = 0;

        localFormulas.forEach(f => {
            const m = (f.name || "").match(regex);
            if (m) {
                const num = m[2] ? parseInt(m[2], 10) : 1;
                if (num > maxNum) maxNum = num;
            }
        });

        const newFormulaNumber = maxNum + 1;
        const newFormulaName = `${baseName} ${newFormulaNumber}`;


        const newFormulaPayload: Omit<Formula, "id"> = {
            name: newFormulaName,
            description: "",
            formula_type: FormulaTypeEnum.STANDARD,
            processing_order: localFormulas.length + 1,
            formula_rows: [],
            creation_timestamp: new Date().toISOString(),
            waterfall_processable_type: WaterfallProcessableTypeEnum.FORMULA,
            transaction_id: this.props.transaction.id,

            formula_variable_id: crypto.randomUUID()
        };

        this.processFetchRequest(
            getRequest_create_formula(newFormulaPayload as Formula),
            (createdFormula: Formula) => {
                if (!createdFormula?.id) return;

                const tempRowId = `newrow-${nextRowId}`;
                const initialRow: FormulaRow = {
                    id: tempRowId,
                    creation_timestamp: new Date().toISOString(),
                    operator: FormulaOperatorEnum.MULTIPLY,
                    factor: 1,
                    variable_id: null,
                    variable_pre_processing: null,
                    processing_order: 1,
                    formula_id: createdFormula.id
                };

                this.registerRowInputs(initialRow);

                this.setState(state => ({
                    localFormulas: [
                        ...state.localFormulas,
                        {...createdFormula, formula_rows: [initialRow]}
                    ],
                    nextRowId: state.nextRowId + 1
                }));

                this.props.loadData?.([ApiEndPoints.FORMULA, ApiEndPoints.VARIABLE]);
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FORMULA_CREATED));
            }
        );
    }


    /**
     * Add a new row to an existing formula
     */
    @autobind
    async handleAddFormulaRow(formulaId: string) {
        const {localFormulas, nextRowId} = this.state;
        const parentFormula = localFormulas.find(t => t.id === formulaId);
        if (!parentFormula || ('isUnsaved' in parentFormula && parentFormula.isUnsaved)) return;
        const tempId = `newrow-${nextRowId}`;
        const newRow: FormulaRow = {
            id: tempId,
            creation_timestamp: new Date().toISOString(),
            operator: FormulaOperatorEnum.MULTIPLY,
            formula_id: formulaId,
            processing_order: (parentFormula.formula_rows?.length || 0) + 1,
            variable_id: null,
            variable_pre_processing: null,
            factor: 1,
        };
        this.registerRowInputs(newRow);

        let newRows = [...(parentFormula.formula_rows || []), newRow];
        newRows = newRows.sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0));

        const updatedFormulas = localFormulas.map(t =>
            t.id === formulaId
                ? {...t, formula_rows: newRows}
                : t
        );

        this.setState(
            state => ({
                localFormulas: updatedFormulas,
                nextRowId: state.nextRowId + 1
            })
        );
        // Try to persist clearing for existing rows
        this.trySaveRowToBackend(formulaId, tempId);
    }

    /**
     * Helper for updating a single formula row's editable fields
     */
    @autobind
    updateRow(formulaId: string, rowId: string, updatedFields: FormulaRowInput) {
        const {localFormulas, rowInputStateIds} = this.state;
        const updatedFormulas = localFormulas.map(t =>
            t.id === formulaId
                ? {
                    ...t,
                    formula_rows: t.formula_rows.map(r =>
                        r.id === rowId ? {...r, ...updatedFields} : r
                    ),
                }
                : t
        );

        const updatedRow = updatedFormulas
            .find(t => t.id === formulaId)!
            .formula_rows.find(r => r.id === rowId)!;

        const ids = rowInputStateIds[rowId];
        if (ids && "variable_id" in updatedFields) {
            this.rowInputStateControl.updateValue(ids.variableId, updatedRow.variable_id || "");
        }

        this.setState({localFormulas: updatedFormulas});
    }

    /**
     * Attempt to save row to backend if it passes validation.
     * On success/new row, updates local state with server response row (ID!).
     */
    private trySaveRowToBackend(formulaId: string, rowId: string) {
        setTimeout(() => {
            const updatedRow = this.state.localFormulas
                .find(t => t.id === formulaId)!
                .formula_rows.find(r => r.id === rowId)!;

            if (rowId.startsWith("newrow-")) {
                // New row: remove temp id, create via backend, update local with server record
                const {id, ...rowWithoutId} = updatedRow;
                this.processFetchRequest(
                    getRequest_create_formula_row(rowWithoutId as Omit<FormulaRow, "id">),
                    (createdRow) => {
                        const nextFormulas = this.state.localFormulas.map(t =>
                            t.id === formulaId
                                ? {...t, formula_rows: t.formula_rows.map(r => r.id === rowId ? createdRow : r)}
                                : t
                        );
                        this.setState({localFormulas: nextFormulas});
                        this.props.loadData([ApiEndPoints.FORMULA]);
                        this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FORMULA_UPDATED));
                    }
                );
            } else {
                this.sendUpdateRow(updatedRow);
            }
        }, 0);
    }

    /**
     * Handles drop event for assigning a variable to a formula row.
     * If the row belongs to a new (unsaved) formula, triggers save of both to backend.
     */
    @autobind
    handleRowDrop(e: React.DragEvent<HTMLTableCellElement>, formulaId: string, rowId: string) {
        e.preventDefault();
        const variableName = e.dataTransfer.getData("variable");
        if (!variableName) return;
        const variableObj = this.props.variables.find(v => v.name === variableName);
        if (!variableObj) return;

        const formula = this.state.localFormulas.find(f => f.id === formulaId);
        if (formula && 'isUnsaved' in formula && formula.isUnsaved) {
            this.saveNewFormulaWithRow(formula, variableObj.id);
        } else {
            const formula = this.state.localFormulas.find(f => f.id === formulaId);
            if (!formula) return;

            const row = formula.formula_rows.find(r => r.id === rowId);
            if (row) {
                this.updateRow(formulaId, rowId, {...row, variable_id: variableObj.id});
            }
            this.trySaveRowToBackend(formulaId, rowId);
        }
    }


    @autobind
    handleVariableDragStart(
        v: Variable,
        e: React.DragEvent<Element>,
        formulaId: string,
        rowId: string
    ) {
        // Keep your existing drag data
        e.dataTransfer.setData("variable", v.name);
        e.dataTransfer.setData("from", "FormulasTab");
        e.dataTransfer.setData("fromFormulaId", formulaId);

        const formula = this.state.localFormulas.find(f => f.id === formulaId);

        if (!formula) {
            // Nothing to do if formula not found
            return;
        }
        if (formula && 'isUnsaved' in formula && formula.isUnsaved) {
            return;
        } else {
            // Clear the variable from this row (and its pre-processing)
            const row = formula.formula_rows.find(r => r.id === rowId);
            if (!row) {
                // Nothing to do if row not found
                return;
            }
            this.updateRow(formulaId, rowId, {
                ...row,
                variable_id: null,
                variable_pre_processing: null
            });

            // Persist clearing for existing rows
            this.trySaveRowToBackend(formulaId, rowId);
        }
    }

    /**
     * Persists a just-created, unsaved formula plus its row (used for variable drop into new formula)
     */
    saveNewFormulaWithRow(formula: Formula, variable_id: string) {
        // Remove isUnsaved and temp id fields before API call
        const {id: _id, ...newFormulaPayload} = formula as Formula & { isUnsaved?: boolean };
        newFormulaPayload.formula_rows = []; // Remove rows for backend creation

        this.processFetchRequest(
            getRequest_create_formula(newFormulaPayload as Formula),
            (createdFormula: Formula) => {
                if (!createdFormula?.id) return;
                const templateRow = formula.formula_rows[0]!;
                // Remove id, patch variable_id/formula_id for backend
                const {id, ...initialRowWithoutId} = {
                    ...templateRow,
                    variable_id,
                    formula_id: createdFormula.id,
                };
                this.processFetchRequest(
                    getRequest_create_formula_row(initialRowWithoutId as Omit<FormulaRow, "id">),
                    (createdRow: FormulaRow) => {
                        // Replace new/unsaved formula with definitive server-side version
                        const filtered = this.state.localFormulas.filter(f => f.id !== formula.id);
                        const toInsert = {...createdFormula, formula_rows: [createdRow]};
                        this.setState({localFormulas: [...filtered, toInsert]});
                        this.props.loadData([ApiEndPoints.VARIABLE, ApiEndPoints.FORMULA]);
                        this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FORMULA_UPDATED));
                    }
                );
            }
        );
    }

    /**
     * Render method: displays the formula list, each as a card/table.
     * Handles drag and drop, modal editing, and row/variable interactions.
     */
    render() {
        const {localFormulas, rowInputStateIds} = this.state;
        const sortedFormulas = localFormulas
            .slice()
            .sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0));

        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea}>
                    <Row>
                        <Col xs={12}>
                            <div className={styles.cardStack}>
                                {sortedFormulas.map((formula) => (
                                    <div
                                        key={formula.id}
                                        onDragOver={e => this.handleFormulaDragOver(e)}
                                        onDrop={e => this.handleFormulaDrop(e, formula.id)}
                                    >
                                        <RBSCard
                                            header={
                                                <TabCardHeader
                                                    icon={"⚡"}
                                                    title={
                                                        <span className={styles.cardHeaderTitleWrap}>
                                                            <span className={styles.cardHeaderTitle}>{formula.name}</span>
                                                            <span className={styles.highlightTag}>[{formula.formula_type}]</span>
                                                        </span>
                                                    }
                                                    actions={
                                                        <div className={styles.headerActions}>
                                                            <RBSButton
                                                                label="Add Row"
                                                                variant={RBSButtonVariants.PRIMARY}
                                                                size={RBSButtonSizes.SMALL}
                                                                onClick={() => this.handleAddFormulaRow(formula.id)}
                                                                disabled={(formula as any).isUnsaved}
                                                            />
                                                            <RBSButton
                                                                label="Edit"
                                                                variant={RBSButtonVariants.SECONDARY}
                                                                size={RBSButtonSizes.SMALL}
                                                                onClick={() => this.openEditFormulaModal(formula)}
                                                                disabled={(formula as any).isUnsaved}
                                                            />
                                                            <RBSButton
                                                                label="Delete"
                                                                variant={RBSButtonVariants.DANGER}
                                                                size={RBSButtonSizes.SMALL}
                                                                onClick={() => this.handleDeleteFormula(formula.id)}
                                                            />
                                                        </div>
                                                    }
                                                    draggable
                                                    onDragStart={e => this.handleFormulaDragStart(e, formula.id)}
                                                />
                                            }
                                            background={RBSCardVariants.Light}
                                            border={RBSCardVariants.Primary}
                                            header_class_name={styles.cardHeaderPrimary}
                                            body_class_name={styles.cardBodyPanel}
                                            container_class_name={styles.roundedCard}
                                        >
                                            {formula.formula_rows && formula.formula_rows.length > 0 ? (
                                                <table className={styles.dataTable}>
                                                    <thead>
                                                    <tr>
                                                        <th className={`${styles.headerPadLg} ${styles.colNarrowSm}`}>Operator</th>
                                                        <th className={`${styles.headerPadLg} ${styles.cellCenter} ${styles.colNarrowSm}`}>Factor</th>
                                                        <th className={`${styles.headerPadLg} ${styles.bracketCell}`}></th>
                                                        <th className={`${styles.headerPadLg} ${styles.colWide}`}>Variable</th>
                                                        <th className={`${styles.headerPadLg} ${styles.cellCenter} ${styles.colNarrowSm}`}>
                                                            Variable pre-processing
                                                        </th>
                                                        <th className={`${styles.headerPadLg} ${styles.bracketCell}`}></th>
                                                        <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}></th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {formula.formula_rows
                                                        .slice()
                                                        .sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0))
                                                        .map((row, idx, arr) => {
                                                            const isSingle = arr.length === 1;
                                                            const isFirst = idx === 0;
                                                            const isLast = idx === arr.length - 1;
                                                            const variableObj = this.props.variables.find(v => v.id === row.variable_id);
                                                            const ids = rowInputStateIds[row.id] || {variableId: -1};
                                                            const validVar = ids.variableId !== -1
                                                                ? this.rowInputStateControl.getValidityForKey(row.id + "-variable")
                                                                : true;
                                                            return (
                                                                <tr
                                                                    key={row.id}
                                                                    className={this.state.hoveredRow === row.id ? styles.tableRowHover : ''}
                                                                    onMouseEnter={() => this.setState({hoveredRow: row.id})}
                                                                    onMouseLeave={() => this.setState({hoveredRow: undefined})}
                                                                >
                                                                    <td className={`${styles.cellPadLg} ${styles.cellCenter} ${styles.colNarrowSm}`}>
                                                                        {isSingle ? (
                                                                            <span>&nbsp;</span>
                                                                        ) : isFirst ? (
                                                                            <span className="fw-bold">(</span>
                                                                        ) : (
                                                                            <select
                                                                                className="form-select text-center"
                                                                                value={row.operator}
                                                                                onChange={e => {
                                                                                    this.updateRow(formula.id, row.id, {
                                                                                        ...row,
                                                                                        operator: e.target.value as any
                                                                                    });
                                                                                    this.trySaveRowToBackend(formula.id, row.id);
                                                                                }}
                                                                            >
                                                                                {Object.entries(operatorLabelMap).map(([key, symbol]) => (
                                                                                    <option key={key}
                                                                                            value={key}>{symbol}</option>
                                                                                ))}
                                                                            </select>
                                                                        )}
                                                                    </td>
                                                                    <td className={`${styles.cellPadLg} ${styles.cellCenter} ${styles.colNarrowSm}`}>
                                                                        <div className="d-flex justify-content-center">
                                                                            <input
                                                                                type="number"
                                                                                className={`form-control form-control-sm ${styles.numericInputMd}`}
                                                                                value={row.factor === undefined ? "" : row.factor}
                                                                                step="any"
                                                                                onChange={e => {
                                                                                    const value = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                                                                    this.updateRow(formula.id, row.id, {...row, factor: value});
                                                                                }}
                                                                                onBlur={() => {
                                                                                    this.trySaveRowToBackend(formula.id, row.id);
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td className={`${styles.cellPadLg} ${styles.cellCenter} ${styles.bracketCell}`}>*</td>
                                                                    <VariableDropCell
                                                                        user_language={this.props.user_language}
                                                                        onDrop={e => this.handleRowDrop(e, formula.id, row.id)}
                                                                        isRequiredMissing={validVar === false}
                                                                        isLastRow={isLast}
                                                                        minWidthPx={120}
                                                                    >
                                                                        {variableObj ? (
                                                                            <VariablesBadges
                                                                                variables={[variableObj]}
                                                                                draggable
                                                                                onDragStart={(v, e) => {
                                                                                    this.setState({
                                                                                        draggedRow: {
                                                                                            formulaId: formula.id,
                                                                                            rowId: row.id,
                                                                                            variableName: v.name,
                                                                                        },
                                                                                    });

                                                                                    this.handleVariableDragStart(v, e, formula.id, row.id);
                                                                                }}
                                                                            />
                                                                        ) : undefined}
                                                                    </VariableDropCell>
                                                                    <td
                                                                        className={styles.cellCenter}
                                                                    >
                                                                        {variableObj ? (
                                                                            <select
                                                                                className="form-select"
                                                                                style={{textAlign: "center"}}
                                                                                // show "---" when variable_pre_processing is null/undefined
                                                                                value={row.variable_pre_processing ?? ""}

                                                                                onChange={e => {
                                                                                    const raw = e.target.value;
                                                                                    const value =
                                                                                        raw === ""
                                                                                            ? null // user selected "---"
                                                                                            : (raw as FormulaVariablePreProcessingTypeEnum);
                                                                                    this.updateRow(formula.id, row.id, {
                                                                                        ...row,
                                                                                        variable_pre_processing: value,
                                                                                    });
                                                                                    this.trySaveRowToBackend(formula.id, row.id);
                                                                                }}
                                                                            >
                                                                                {/* Placeholder option shown first */}
                                                                                <option value="">---</option>

                                                                                {/* Enum-based options */}
                                                                                {
                                                                                    Object.entries(variablePreProcessingLabelMap).map(([key, symbol]) => (
                                                                                        <option key={key}
                                                                                                value={key}>{symbol}</option>
                                                                                    ))}
                                                                            </select>
                                                                        ) : (
                                                                            <span className="text-muted">First add variable</span>
                                                                        )}
                                                                    </td>
                                                                    {isLast && !isSingle ? (
                                                                        <td className={`${styles.cellPadLg} ${styles.bracketCell}`}>{")"}</td>
                                                                    ) : (
                                                                        <td className={`${styles.cellPadLg} ${styles.bracketCell}`}/>
                                                                    )}
                                                                    <td className={`${styles.cellPadLg} ${styles.actionCell}`}>
                                                                        <div className={styles.hoverButtons}>
                                                                            <RBSButton
                                                                                label="🗑️"
                                                                                title="Delete"
                                                                                onClick={() => {
                                                                                    this.handleDeleteFormulaRow(row.id);
                                                                                }}
                                                                                variant={RBSButtonVariants.DANGER}
                                                                                size={RBSButtonSizes.SMALL}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <EmptyStateRow
                                                    message={'No rows yet. Use "Add Row" or drop a variable.'}/>
                                            )}
                                        </RBSCard>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>

                    <div className={styles.toolbarBottom}>
                        <RBSButton
                            label="Add Formula"
                            variant={RBSButtonVariants.PRIMARY}
                            size={RBSButtonSizes.LARGE}
                            onClick={this.handleAddFormula}
                            title="Add Formula"
                        />
                    </div>
                </div>

                {/* Modal dialog for editing formulas */}
                {this.state.editingFormula && <FormulaEditModal
                    show={this.state.showEditFormulaModal}
                    formula={this.state.editingFormula}
                    onSubmit={this.handleEditFormulaSubmit}
                    onClose={this.closeEditFormulaModal}
                    isValid={this.state.editingFormulaValid}
                    inputStateControl={this.editingFormulaInputControl}
                    user_language={this.props.user_language}
                />}
            </div>
        );
    }
}
